import { useEffect, useState } from 'react';
import {
  useRouteError,
  useNavigate,
  isRouteErrorResponse,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Home, ArrowLeft } from 'lucide-react';
import { UpdatingNotice } from '@/shared/ui/UpdatingNotice';
import {
  isStaleChunkError,
  reloadOnceForStaleChunk,
} from '@/shared/utils/staleChunk';

export default function RouteError() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation('routeError');
  const isStaleChunk = isStaleChunkError(error);
  const [recoveryFailed, setRecoveryFailed] = useState(false);

  // The tab is running a bundle the origin no longer serves (SP-1330): reload
  // once rather than leaving the user on the error screen.
  useEffect(() => {
    if (!isStaleChunk) {
      return;
    }
    if (!reloadOnceForStaleChunk()) {
      setRecoveryFailed(true);
    }
  }, [isStaleChunk]);

  if (isStaleChunk && !recoveryFailed) {
    return <UpdatingNotice />;
  }

  let statusCode = 'Error';
  let title = t('somethingWentWrong');
  let message = t('unexpectedError');
  let icon = '⚠️';

  if (isRouteErrorResponse(error)) {
    statusCode = error.status?.toString() || 'Error';

    if (error.status === 404) {
      title = t('pageNotFound');
      message = t('pageNotFoundMessage');
      icon = '🔍';
    } else if (error.status === 403) {
      title = t('accessDenied');
      message = t('accessDeniedMessage');
      icon = '🔒';
    } else if (error.status === 500) {
      title = t('serverError');
      message = t('serverErrorMessage');
      icon = '💥';
    } else {
      title = error.statusText || title;
      message = error.data?.message || message;
    }
  } else if (error instanceof Error) {
    title = t('applicationError');
    message = error.message;
    icon = '❌';
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 px-4 sm:px-6 lg:px-8">
      {/* Decorative circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-10 left-10 w-72 h-72 bg-blue-200 dark:bg-blue-900/20 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-purple-200 dark:bg-purple-900/20 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-md w-full text-center">
        {/* Status Code */}
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
            <span className="text-5xl">{icon}</span>
          </div>
          <h1 className="text-7xl sm:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-600 dark:from-red-700 dark:to-red-800 mb-4">
            {statusCode}
          </h1>
        </div>

        {/* Title */}
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
          {title}
        </h2>

        {/* Message */}
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
          {message}
        </p>

        {/* Error Details in Development */}
        {import.meta.env.MODE === 'development' && (
          <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-left">
            <div className="flex items-start gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-semibold text-red-800 dark:text-red-200">
                {t('developmentInfo')}
              </p>
            </div>
            <pre className="text-xs text-red-700 dark:text-red-300 overflow-auto max-h-40 bg-red-100/50 dark:bg-red-900/40 p-3 rounded font-mono">
              {error instanceof Error
                ? error.stack
                : JSON.stringify(error, null, 2)}
            </pre>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-2 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 active:scale-95 sm:w-auto dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('goBack')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-gray-700 active:scale-95 sm:w-auto dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
          >
            <Home className="h-4 w-4" />
            {t('goHome')}
          </button>
        </div>

        {/* Footer Message */}
        <p className="mt-8 text-sm text-gray-500 dark:text-gray-400">
          {t('contactSupport')}
        </p>
      </div>
    </div>
  );
}
